import React, { useEffect, useMemo, useRef, useState } from 'react';
import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import { DeleteIcon } from 'assets/icons';
import ListDropDown from 'components/list-dropdown/ListDropDown';
import { MultiSelectDropdown } from 'components';
import { JobLeadSourceTypes } from 'containers/jobs/constant';
import {
  CommissionRuleConditionIf,
  CommissionRuleConditionIfDisplay,
  CommissionRuleConditionOp,
  CommissionRuleConditionOpDisplay
} from '../commission-settings/constants';
import { ExternalCompanyResponse } from '../external-company-settings/externalCompany.model';
import { VoidFn } from 'types/baseTypes';
import { PaymentMode } from '../invoiceSettings.model';
import { useAppSelector } from 'hooks';
import { selectMerchantCustomizations } from 'containers/app/appSlice';

const conditionOpOptions = [
  { label: CommissionRuleConditionOpDisplay.IS_ANY_OF, value: CommissionRuleConditionOp.IS_ANY_OF },
  { label: CommissionRuleConditionOpDisplay.IS_NONE_OF, value: CommissionRuleConditionOp.IS_NONE_OF }
];

interface CommissionCondition {
  conditionIf: string;
  conditionOp: string;
  conditionValue: string[];
}

interface CommissionConditionsProps {
  conditions: CommissionCondition[];
  onConditionsChange: (conditions: CommissionCondition[]) => void;
  externalCompanies: ExternalCompanyResponse[];
  paymentMethods: PaymentMode[];
  handleOnFocus: VoidFn;
}

const CommissionConditions: React.FC<CommissionConditionsProps> = ({
  conditions,
  onConditionsChange,
  externalCompanies,
  paymentMethods,
  handleOnFocus
}) => {
  const [dashedLineHeight, setDashedLineHeight] = useState<number>(0);
  const conditionsContainerRef = useRef<HTMLDivElement>(null);
  const { teamConfigs } = useAppSelector(selectMerchantCustomizations) || {};
  const { hasPaymentMethodInCommissionRules } = teamConfigs || {};

  const conditionIfOptions = [
    { id: CommissionRuleConditionIf.LEAD_SOURCE, name: CommissionRuleConditionIfDisplay.LEAD_SOURCE },
    { id: CommissionRuleConditionIf.EXTERNAL_COMPANY_ID, name: CommissionRuleConditionIfDisplay.EXTERNAL_COMPANY_ID },
    ...(hasPaymentMethodInCommissionRules
      ? [{ id: CommissionRuleConditionIf.PAYMENT_METHOD, name: CommissionRuleConditionIfDisplay.PAYMENT_METHOD }]
      : [])
  ];

  const valueOptions = useMemo(() => {
    return {
      [CommissionRuleConditionIf.LEAD_SOURCE]: JobLeadSourceTypes.map(source => ({
        id: source.label,
        label: source.label
      })),
      [CommissionRuleConditionIf.EXTERNAL_COMPANY_ID]: externalCompanies?.map(company => ({
        id: company.id,
        label: company.companyName
      })),
      [CommissionRuleConditionIf.PAYMENT_METHOD]: paymentMethods
        ?.filter(method => method.adminEnabled && method.teamLevelVisiblity)
        .map(method => ({
          id: method.name,
          label: method.label
        }))
    };
  }, [externalCompanies, paymentMethods]);

  // Use ResizeObserver to track height changes of the conditions container
  useEffect(() => {
    if (!conditionsContainerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (conditionsContainerRef.current) {
        const containerHeight = (conditionsContainerRef.current as HTMLDivElement).offsetHeight;
        const lastConditionHeight =
          (conditionsContainerRef.current.lastElementChild as HTMLDivElement)?.offsetHeight || 0;
        setDashedLineHeight(containerHeight - lastConditionHeight);
      }
    });

    resizeObserver.observe(conditionsContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [conditions.length]);

  const addCondition = () => {
    onConditionsChange([...conditions, { conditionIf: '', conditionOp: 'IS_ANY_OF', conditionValue: [] }]);
    handleOnFocus();
  };

  const deleteCondition = (indexToDelete: number) => {
    onConditionsChange(conditions.filter((_, index) => index !== indexToDelete));
  };

  const handleConditionChange = (index: number, field: string, value: any) => {
    const newConditions = [...conditions];
    newConditions[index][field] = value;
    if (field === 'conditionIf') {
      newConditions[index].conditionValue = [];
    }
    onConditionsChange(newConditions);
  };

  if (conditions.length === 0) {
    return (
      <div className="rounded-md border border-borderGray p-4">
        <div
          onClick={addCondition}
          className="flex cursor-pointer items-center gap-2 text-17px font-semibold text-secondary">
          <LineItemPlusReactIcon id="add-condition" className="shrink-0" />
          Add new condition
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative">
        {/* Dashed line container */}
        {conditions.length > 1 && (
          <div
            className="absolute left-6 border-l-2 border-dashed border-borderGray"
            style={{
              top: '44px',
              height: `${dashedLineHeight - 20}px`
            }}
          />
        )}

        {/* Conditions list */}
        <div className="space-y-4" ref={conditionsContainerRef}>
          {conditions.map((condition, index) => {
            const isLast = index === conditions.length - 1;
            const conditionIfValue = conditionIfOptions.find(opt => opt.id === condition.conditionIf);

            return (
              <div key={index} className="relative">
                <div className="grid grid-cols-[50px_1fr] gap-2">
                  <div className="relative flex justify-center">
                    <span className="mt-4 h-fit bg-white px-1 text-sbase font-semibold text-primaryText">If</span>
                  </div>
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="w-48">
                      <ListDropDown
                        id={`condition-if-${index}`}
                        items={conditionIfOptions}
                        selectedItem={conditionIfValue}
                        setSelectedItem={item => handleConditionChange(index, 'conditionIf', item.id)}
                        placeholder="choose property"
                        buttonStyles="bg-secondaryBtn h-[50px] text-[17px] font-semibold text-primaryText
                        px-[10px] py-[14px]"
                        itemStyle="font-normal text-[17px] tracking-[-0.3px] leading-[28px]"
                        spanStyles={`font-normal text-[17px] leading-[21px] ${
                          conditionIfValue ? 'text-primaryText' : 'text-accentText opacity-70'
                        }`}
                        iconStyle="right-[10px]"
                      />
                    </div>

                    <div className="mt-1 flex h-[50px] overflow-hidden rounded-md border border-borderGray">
                      {conditionOpOptions.map(op => (
                        <button
                          key={op.value}
                          className={`h-full px-4 text-[17px] first-of-type:border-r lg:w-[106px] ${
                            condition.conditionOp === op.value
                              ? 'bg-[#A4D7FA] font-semibold text-primaryText'
                              : 'font-normal text-accentText'
                          }`}
                          onClick={() => handleConditionChange(index, 'conditionOp', op.value)}>
                          {op.label}
                        </button>
                      ))}
                    </div>

                    <div className="mt-1 w-[300px]">
                      <MultiSelectDropdown
                        selectedValues={condition.conditionValue}
                        options={valueOptions[condition.conditionIf] || []}
                        onChange={values => handleConditionChange(index, 'conditionValue', values)}
                        placeholder="choose property"
                        buttonStyle={`${
                          condition?.conditionValue.length > 0
                            ? 'text-primaryText py-2'
                            : 'py-[14px] text-accentText opacity-70'
                        } bg-secondaryBtn text-[17px] font-normal`}
                        disabled={!condition.conditionIf}
                      />
                    </div>

                    <button onClick={() => deleteCondition(index)} className="mt-3 rounded-md p-2 hover:bg-gray-100">
                      <DeleteIcon className="h-5 w-5 text-red-500" />
                    </button>
                  </div>
                </div>

                {!isLast && (
                  <div className="mt-4 grid grid-cols-[50px_1fr] gap-2">
                    <div className="relative flex justify-center">
                      <span className="z-10 bg-white px-1 text-sm italic text-accentText">and</span>
                    </div>
                    <div />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div
        onClick={addCondition}
        className="flex cursor-pointer items-center gap-2 rounded-md border
         border-borderGray p-4 text-17px font-semibold text-secondary">
        <LineItemPlusReactIcon id="add-condition" className="shrink-0" />
        Add new condition
      </div>
    </div>
  );
};

export default CommissionConditions;
